// src/pages/admin/AdminUsersList.jsx
//
// Founder-only engagement dashboard at /admin/users. Answers: who signed
// up, who's active, who's stale, who never got past onboarding.
//
// Data sources (all already populated by the existing auth + onboarding
// flows; no new writes needed):
//   • users/{uid}.createdAt          — signup timestamp
//   • users/{uid}.presence.lastSeenAt — touched on every auth resolve
//   • users/{uid}.lastActive          — touched via onUserSignIn callable
//   • users/{uid}.onboardingStatus / onboardingComplete
//   • users/{uid}.teamIds / teams / memberships
//
// See features/admin-users/01-spec.md.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { db } from '@/firebase/firebase';
import { usePlatformClaims } from '@/hooks/usePlatformClaims';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  UsersIcon,
  CheckBadgeIcon,
  CalendarDaysIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';

const PAGE_CAP = 500;
const HEAD_FONT = "'Barlow Condensed', sans-serif";

const ACTIVE_7D_MS  = 7  * 24 * 60 * 60 * 1000;
const ACTIVE_30D_MS = 30 * 24 * 60 * 60 * 1000;

const FILTERS = [
  { key: 'all',           label: 'All' },
  { key: 'active7',       label: '🟢 Active 7d' },
  { key: 'active30',      label: '🟢 Active 30d' },
  { key: 'onboarded',     label: '✅ Onboarded' },
  { key: 'not_onboarded', label: '⏳ Not onboarded' },
  { key: 'this_month',    label: '📅 Signed up this month' },
];

function toMs(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.toDate === 'function')   return ts.toDate().getTime();
  if (ts.seconds) return ts.seconds * 1000;
  return Number(ts) || 0;
}

function startOfMonthMs() {
  const d = new Date();
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
}

function relativeAgo(ms) {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  if (diff < 0) return 'in the future';
  const mins = Math.floor(diff / 60000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30)  return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function displayName(u) {
  const full = `${u.firstName || ''} ${u.lastName || ''}`.trim();
  if (full) return full;
  if (u.displayName) return u.displayName;
  if (u.email) return u.email.split('@')[0];
  return u.id?.slice(0, 8) || 'Unknown';
}

function teamsCount(u) {
  return (
    (Array.isArray(u.teamIds)     && u.teamIds.length)     ||
    (Array.isArray(u.teams)       && u.teams.length)       ||
    (Array.isArray(u.memberships) && u.memberships.length) ||
    0
  );
}

function lastSeenMs(u) {
  return Math.max(
    toMs(u.presence?.lastSeenAt),
    toMs(u.lastActive),
  );
}

function healthBadge(u) {
  const last = lastSeenMs(u);
  if (!last) return { tone: 'never',   label: '⚪ Never' };
  const age = Date.now() - last;
  if (age < ACTIVE_7D_MS)  return { tone: 'active',  label: '🟢 Active' };
  if (age < ACTIVE_30D_MS) return { tone: 'stale',   label: '🟡 Stale' };
  return                          { tone: 'dormant', label: '🔴 Dormant' };
}

// Onboarding-recovery stage labels (features/onboarding-recovery/, BA §9).
const STAGE_SHORT = { 1: 'Account', 3: 'Team', 4: 'Season', 5: 'Link', 6: 'Parents', 7: 'Message' };

function onboardingBadge(u) {
  // Preferred: the cached stage written by adminComputeOnboardingStages.
  // Falls back to the legacy coarse badge until the first sweep runs (N17).
  const ob = u.onboarding;
  if (ob?.stage) {
    if (ob.activated) return { tone: 'done', label: '✅ Activated' };
    const name = STAGE_SHORT[ob.stage] || `Stage ${ob.stage}`;
    return { tone: 'starting', label: `⏳ ${ob.stage} · ${name}` };
  }
  if (u.onboardingComplete === true) return { tone: 'done',     label: '✅ Onboarded' };
  const status = (u.onboardingStatus || '').toLowerCase();
  if (status === 'completed')         return { tone: 'done',     label: '✅ Onboarded' };
  if (status === 'registered')        return { tone: 'starting', label: '⏳ Registered' };
  if (status === 'in_progress')       return { tone: 'starting', label: '⏳ In progress' };
  return { tone: 'unknown', label: '— —' };
}

const HEALTH_CLASS = {
  active:  'bg-lime-100 text-lime-800 border-lime-200',
  stale:   'bg-amber-100 text-amber-800 border-amber-200',
  dormant: 'bg-red-100 text-red-800 border-red-200',
  never:   'bg-zinc-100 text-zinc-600 border-zinc-200',
};

const ONBOARDING_CLASS = {
  done:     'bg-lime-100 text-lime-800 border-lime-200',
  starting: 'bg-amber-100 text-amber-800 border-amber-200',
  unknown:  'bg-zinc-100 text-zinc-600 border-zinc-200',
};

export default function AdminUsersList() {
  const { isFounder } = usePlatformClaims();
  const blocked = !isFounder;

  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState('all');
  const [sweeping, setSweeping] = useState(false);
  const [rollup, setRollup]     = useState(null);

  // Onboarding-recovery sweep: recomputes + caches every user's stage
  // server-side, returns the funnel rollup. Founder-only callable.
  async function runSweep() {
    setSweeping(true);
    setError(null);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'adminComputeOnboardingStages');
      const res = await fn({});
      setRollup(res.data);
      await loadUsers(); // reload so the cached chips reflect the sweep
    } catch (e) {
      setError(e?.message || 'Stage sweep failed.');
    } finally {
      setSweeping(false);
    }
  }

  async function loadUsers() {
    setLoading(true);
    setError(null);
    try {
      // Query bounded; order by createdAt desc when available. A few legacy
      // user docs may lack createdAt — those will be omitted by Firestore's
      // orderBy. Acceptable for v1 (the missing rows are pre-pivot test
      // accounts; the user-doc backfill stamps createdAt on new signups).
      const q = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        limit(PAGE_CAP),
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUsers(rows);
    } catch (e) {
      setError(e?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (blocked) return;
    loadUsers();
  }, [blocked]);

  // ── Derived ─────────────────────────────────────────────────────────

  const monthStart = useMemo(startOfMonthMs, []);

  const stats = useMemo(() => {
    const out = { total: 0, active7: 0, signupsThisMonth: 0, onboarded: 0 };
    for (const u of users) {
      out.total += 1;
      const last = lastSeenMs(u);
      if (last && Date.now() - last < ACTIVE_7D_MS) out.active7 += 1;
      const created = toMs(u.createdAt);
      if (created >= monthStart) out.signupsThisMonth += 1;
      if (u.onboardingComplete === true ||
          (u.onboardingStatus || '').toLowerCase() === 'completed') {
        out.onboarded += 1;
      }
    }
    return out;
  }, [users, monthStart]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return users.filter((u) => {
      // Filter chip
      if (filter !== 'all') {
        const last = lastSeenMs(u);
        const age  = last ? Date.now() - last : Infinity;
        const created = toMs(u.createdAt);
        const onboardedBool =
          u.onboardingComplete === true ||
          (u.onboardingStatus || '').toLowerCase() === 'completed';

        if (filter === 'active7'       && !(last && age < ACTIVE_7D_MS))  return false;
        if (filter === 'active30'      && !(last && age < ACTIVE_30D_MS)) return false;
        if (filter === 'onboarded'     && !onboardedBool)                 return false;
        if (filter === 'not_onboarded' && onboardedBool)                  return false;
        if (filter === 'this_month'    && !(created >= monthStart))       return false;
      }

      // Search
      if (needle) {
        const hay = [
          u.firstName, u.lastName, u.displayName, u.email,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [users, search, filter, monthStart]);

  if (blocked) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 mt-0.5" />
          <p>
            <span className="font-semibold">Insufficient privilege.</span>{' '}
            Founder claim required to view user engagement.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="flex items-center justify-between mb-5">
        <h1
          className="text-3xl font-bold uppercase tracking-tight text-zinc-900"
          style={{ fontFamily: HEAD_FONT }}
        >
          Users
        </h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={runSweep}
            disabled={sweeping || loading}
            title="Recompute every user's onboarding stage server-side"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold border border-zinc-300 text-zinc-800 hover:bg-zinc-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${sweeping ? 'animate-spin' : ''}`} />
            {sweeping ? 'Sweeping…' : 'Refresh stages'}
          </button>
          <button
            type="button"
            onClick={loadUsers}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-100 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Funnel rollup (populated after a stage sweep) */}
      {rollup && (
        <div className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 mb-5">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-600">
            <span className="font-bold uppercase tracking-wide text-zinc-500">Funnel</span>
            {Object.entries(rollup.rollup || {})
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([stage, count]) => (
                <span key={stage} className="inline-flex items-center gap-1">
                  <strong className="text-zinc-900">{count}</strong>
                  at {stage} · {STAGE_SHORT[stage] || stage}
                </span>
              ))}
            <span className="inline-flex items-center gap-1 text-lime-700">
              <strong>{rollup.activated ?? 0}</strong> activated
            </span>
            {rollup.warnings > 0 && (
              <span className="text-amber-700">{rollup.warnings} with data warnings</span>
            )}
            <span className="ml-auto text-zinc-400">{rollup.scanned} scanned</span>
          </div>
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <KPI icon={<UsersIcon className="h-5 w-5" />}        label="Total"             value={loading ? null : stats.total} />
        <KPI icon={<BoltIcon className="h-5 w-5" />}         label="Active (7d)"       value={loading ? null : stats.active7} />
        <KPI icon={<CalendarDaysIcon className="h-5 w-5" />} label="Signed up (mo)"    value={loading ? null : stats.signupsThisMonth} />
        <KPI icon={<CheckBadgeIcon className="h-5 w-5" />}   label="Onboarded"         value={loading ? null : stats.onboarded} />
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400 pointer-events-none" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full rounded-md border border-zinc-200 pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-400"
        />
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                active
                  ? 'bg-lime-400 border-lime-500 text-zinc-900'
                  : 'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50'
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <p className="text-xs text-zinc-500 mb-2">
        Showing {filtered.length} of {users.length} users
        {users.length === PAGE_CAP && ` (capped at ${PAGE_CAP})`}
      </p>

      {/* Error */}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-start gap-2 mb-4">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold">Couldn't load users</p>
            <p className="text-red-700">{error}</p>
          </div>
          <button
            type="button"
            onClick={loadUsers}
            className="px-2 py-1 rounded text-xs font-semibold border border-red-300 text-red-800 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && !error && (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-12 rounded-md bg-zinc-100 animate-pulse" />
          ))}
        </div>
      )}

      {/* Empty */}
      {!loading && !error && users.length === 0 && (
        <div className="rounded-2xl border border-zinc-200 bg-white px-6 py-12 text-center text-sm text-zinc-500">
          No users yet.
        </div>
      )}
      {!loading && !error && users.length > 0 && filtered.length === 0 && (
        <div className="rounded-2xl border border-zinc-200 bg-white px-6 py-8 text-center text-sm text-zinc-500">
          No users match the current filters.
        </div>
      )}

      {/* Table (desktop) */}
      {!loading && !error && filtered.length > 0 && (
        <>
          <div className="hidden sm:block rounded-2xl border border-zinc-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left text-xs font-semibold uppercase tracking-widest text-zinc-500">
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Onboarding</th>
                  <th className="px-4 py-2">Signed up</th>
                  <th className="px-4 py-2">Last seen</th>
                  <th className="px-4 py-2">Teams</th>
                  <th className="px-4 py-2">Health</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const ob = onboardingBadge(u);
                  const hb = healthBadge(u);
                  return (
                    <tr key={u.id} className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50">
                      <td className="px-4 py-2 font-semibold text-zinc-900">
                        <Link to={`/admin/users/${u.id}`} className="hover:underline">
                          {displayName(u)}
                        </Link>
                      </td>
                      <td className="px-4 py-2 text-zinc-700 text-xs">
                        {u.email || '—'}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border ${ONBOARDING_CLASS[ob.tone]}`}>
                          {ob.label}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-zinc-600">{relativeAgo(toMs(u.createdAt))}</td>
                      <td className="px-4 py-2 text-zinc-600">{relativeAgo(lastSeenMs(u))}</td>
                      <td className="px-4 py-2 text-zinc-700 tabular-nums">{teamsCount(u)}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border ${HEALTH_CLASS[hb.tone]}`}>
                          {hb.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Stacked cards (mobile) */}
          <div className="sm:hidden space-y-2">
            {filtered.map((u) => {
              const ob = onboardingBadge(u);
              const hb = healthBadge(u);
              return (
                <Link
                  key={u.id}
                  to={`/admin/users/${u.id}`}
                  className="block rounded-2xl border border-zinc-200 bg-white px-4 py-3 hover:bg-zinc-50"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-zinc-900">{displayName(u)}</p>
                      <p className="text-xs text-zinc-500 truncate">{u.email || '—'}</p>
                    </div>
                    <span className={`shrink-0 inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border ${HEALTH_CLASS[hb.tone]}`}>
                      {hb.label}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-zinc-600">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border ${ONBOARDING_CLASS[ob.tone]}`}>
                      {ob.label}
                    </span>
                    <span>·</span>
                    <span>Signed up {relativeAgo(toMs(u.createdAt))}</span>
                    <span>·</span>
                    <span>Last seen {relativeAgo(lastSeenMs(u))}</span>
                    <span>·</span>
                    <span>{teamsCount(u)} teams</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ icon, label, value }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white px-4 py-3.5">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">
        <span className="text-lime-600">{icon}</span>
        <span>{label}</span>
      </div>
      {value == null ? (
        <div className="mt-2 h-9 w-20 rounded-md bg-zinc-100 animate-pulse" />
      ) : (
        <div
          className="mt-1 text-3xl font-bold tabular-nums text-zinc-900"
          style={{ fontFamily: HEAD_FONT }}
        >
          {value}
        </div>
      )}
    </div>
  );
}
